import type { LicenceResponse, SendBulkInvitationResponse, UserResponse } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import { logger } from '@/utils/logger';

import { validateAddDraft } from '../domain/add-user.validation';
import type { AddUserDraft } from '../domain/add-user-draft.model';
import { toAddUserError } from '../domain/add-user-error';
import type { AddUserGateway } from '../infrastructure/add-user.gateway';
import { licencesRequested, submitRequested } from './add-user.actions';
import { selectDraft } from './add-user.selectors';
import { addUserActions } from './add-user.slice';

/**
 * WORKER: load the licences for the picker, on mount and again once a licence
 * has been created from it (#593). Best-effort, exactly like the original page
 * (which swallowed a licence-load failure): on error we settle with an empty list
 * rather than blocking the form. All I/O via the injected gateway.
 */
function* loadLicencesWorker() {
  const gateway: AddUserGateway = yield getContext('addUserGateway');
  try {
    const licences = (yield call(gateway.fetchLicences)) as LicenceResponse[];
    yield put(addUserActions.licencesLoaded(licences));
  } catch {
    yield put(addUserActions.licencesLoaded([]));
  }
}

/**
 * Invite the user just created (#594). Never throws: the user exists whatever
 * happens here, so a failure (the request failing, or the id coming back in
 * `failed` or in neither list) is reported as `false`, and the user stays in
 * Not invited, to be invited from the index.
 */
function* inviteCreatedUser(gateway: AddUserGateway, userId: string) {
  try {
    const result = (yield call(gateway.inviteUser, userId)) as SendBulkInvitationResponse['data'];
    return result.sent.includes(userId);
  } catch (e) {
    logger.warn('Invitation of a newly created user failed', e);
    return false;
  }
}

/**
 * WORKER: create the user, then invite them. Validation runs first (pure
 * domain); on failure it dispatches a `validation` business error and stops
 * WITHOUT touching the gateway. Otherwise all I/O goes through the injected
 * gateway. Only the creation can fail the save; the invitation's outcome rides
 * on `saveSucceeded`. `takeLatest` means a re-submit supersedes an in-flight one.
 */
function* saveWorker() {
  const draft = (yield select(selectDraft)) as AddUserDraft;

  const errors = validateAddDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(addUserActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: AddUserGateway = yield getContext('addUserGateway');
  yield put(addUserActions.saveStarted());
  try {
    const user = (yield call(gateway.createUser, draft)) as UserResponse;
    const invitationSent = (yield call(inviteCreatedUser, gateway, user.id)) as boolean;
    yield put(addUserActions.saveSucceeded({ email: user.email, invitationSent }));
  } catch (e) {
    yield put(addUserActions.saveFailed(toAddUserError(e)));
  }
}

/** WATCHERS. */
export function* addUserSaga() {
  yield all([
    // A reload after a create supersedes a mount load still in flight.
    takeLatest([licencesRequested.type, addUserActions.licenceCreated.type], loadLicencesWorker),
    takeLatest(submitRequested.type, saveWorker),
  ]);
}
